import React, { useEffect, useId, useRef, useState } from "react";
import { MdCheck, MdKeyboardArrowDown } from "react-icons/md";
import { FormField, getControlClassName } from "components/fields";
import { AVAILABLE_FONTS, useGoogleFonts } from "views/public/link-hub/fonts";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const FONT_GROUPS = [
  { label: "Sans-Serif", fonts: AVAILABLE_FONTS.filter((f) => f.category === "sans-serif") },
  { label: "Serif", fonts: AVAILABLE_FONTS.filter((f) => f.category === "serif") },
];
const ORDERED_FONTS = FONT_GROUPS.flatMap((group) => group.fonts);
const ALL_FAMILIES = ORDERED_FONTS.map((f) => f.value);

// Mounted only while the picker is open, so every family is fetched on first open only.
const FontMenu = ({ listId, labelId, value, onSelect, onClose }) => {
  useGoogleFonts(ALL_FAMILIES);
  const listRef = useRef(null);
  const optionRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(() =>
    Math.max(0, ORDERED_FONTS.findIndex((f) => f.value === value))
  );

  useEffect(() => {
    listRef.current?.focus();
  }, []);

  useEffect(() => {
    optionRefs.current[activeIndex]?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex]);

  const handleKeyDown = (e) => {
    const last = ORDERED_FONTS.length - 1;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, last));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case "Home":
        e.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        e.preventDefault();
        setActiveIndex(last);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        onSelect(ORDERED_FONTS[activeIndex].value);
        break;
      case "Escape":
        e.preventDefault();
        onClose({ restoreFocus: true });
        break;
      case "Tab":
        onClose({ restoreFocus: false });
        break;
      default:
    }
  };

  let index = -1;
  return (
    <div
      ref={listRef}
      id={listId}
      role="listbox"
      tabIndex={-1}
      aria-labelledby={labelId}
      aria-activedescendant={`${listId}-option-${activeIndex}`}
      onKeyDown={handleKeyDown}
      className="absolute left-0 top-full z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-control border border-line bg-surface py-1 shadow-overlay focus:outline-none"
    >
      {FONT_GROUPS.map((group) => {
        const groupLabelId = `${listId}-${group.label}`;
        return (
          <ul key={group.label} role="group" aria-labelledby={groupLabelId}>
            <li
              id={groupLabelId}
              role="presentation"
              className="sticky top-0 bg-surface-subtle px-3 py-1.5 text-2xs font-semibold uppercase tracking-wide text-fg-muted"
            >
              {group.label}
            </li>
            {group.fonts.map((font) => {
              index += 1;
              const optionIndex = index;
              const selected = font.value === value;
              const active = optionIndex === activeIndex;
              return (
                <li
                  key={font.value}
                  ref={(el) => {
                    optionRefs.current[optionIndex] = el;
                  }}
                  id={`${listId}-option-${optionIndex}`}
                  role="option"
                  aria-selected={selected}
                  onClick={() => onSelect(font.value)}
                  className={cx(
                    "flex cursor-pointer items-center justify-between px-3 py-2 text-base text-fg",
                    selected ? "bg-brand-50 dark:bg-brand-900/40" : "hover:bg-surface-subtle",
                    active && "ring-2 ring-inset ring-focus"
                  )}
                >
                  <span style={{ fontFamily: font.value }}>{font.name}</span>
                  {selected && (
                    <MdCheck aria-hidden="true" className="h-5 w-5 text-brand-700 dark:text-brand-200" />
                  )}
                </li>
              );
            })}
          </ul>
        );
      })}
    </div>
  );
};

const FontPicker = ({ label, value, onChange, previewText }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const baseId = useId();
  const triggerId = `${baseId}-trigger`;
  const labelId = `${baseId}-label`;
  const listId = `${baseId}-listbox`;

  const selectedFont = AVAILABLE_FONTS.find((f) => f.value === value) || AVAILABLE_FONTS[0];

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const close = ({ restoreFocus }) => {
    setIsOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  };

  const select = (fontValue) => {
    onChange(fontValue);
    close({ restoreFocus: true });
  };

  return (
    <FormField
      id={triggerId}
      label={<span id={labelId}>{label}</span>}
      hint={<span style={{ fontFamily: value }}>Preview: {previewText}</span>}
    >
      {(controlProps) => (
        <div ref={containerRef} className="relative">
          <button
            ref={triggerRef}
            type="button"
            {...controlProps}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls={isOpen ? listId : undefined}
            onClick={() => setIsOpen((open) => !open)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown" && !isOpen) {
                e.preventDefault();
                setIsOpen(true);
              }
            }}
            className={getControlClassName({
              className: "flex items-center justify-between text-left",
            })}
          >
            <span style={{ fontFamily: selectedFont.value }}>{selectedFont.name}</span>
            <MdKeyboardArrowDown
              aria-hidden="true"
              className={cx("h-5 w-5 text-fg-muted transition-transform", isOpen && "rotate-180")}
            />
          </button>
          {isOpen && (
            <FontMenu
              listId={listId}
              labelId={labelId}
              value={value}
              onSelect={select}
              onClose={close}
            />
          )}
        </div>
      )}
    </FormField>
  );
};

export default FontPicker;
